import { useEffect } from "react";
import { Redirect, Route, type RouteProps } from "react-router-dom";
import { useCustomRouter } from "shared/context/CustomRouterContext";
import { useCustomSession } from "shared/context/CustomSessionContext";
import RouteConfigWrapper, {
  type HubRouteConfig,
} from "hub/src/routes/RouteConfigWrapper";
import { hasRouteAccess } from "hub/src/utils/routes/hasRouteAccess";

type Props = RouteProps & {
  children?: React.ReactNode;
  checkPermission?: boolean;
  routeConfig?: HubRouteConfig;
};

// Custom redirect component that uses the router.push method
const CustomRedirectToLogin = () => {
  const router = useCustomRouter();

  useEffect(() => {
    const propsToPush = {
      ...router.state,
      redirectUrlAfterLogin: router.pathname,
    };
    router.push("/login", propsToPush);
    // We only want to push once when the component mounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return null;
};

const ProtectedRoute: React.FC<Props> = ({
  children,
  render,
  routeConfig,
  ...props
}) => {
  const hubSessionContext = useCustomSession();

  if (!hubSessionContext.societyUserId) {
    return <CustomRedirectToLogin />;
  }

  //Public Routes are publicly advertised but still may have permission tags so we need to check for permission tags again.
  if (
    !!routeConfig &&
    !hasRouteAccess({ session: hubSessionContext, route: routeConfig })
  ) {
    return <Redirect to="/access-denied" />;
  }

  return (
    <Route
      {...props}
      render={(props) => (
        <RouteConfigWrapper>
          {render ? render(props) : children}
        </RouteConfigWrapper>
      )}
    />
  );
};

export default ProtectedRoute;
